import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MapPin, Sparkles, Clock, Car } from "lucide-react";
import useViewTracker from "../../hooks/useViewTracker";
import SEO from "../../components/SEO";
import SmartImage from "../../components/SmartImage";
import { useData } from "../../context/DataContext";

const PreviewDestinations = () => {
    const { t } = useTranslation();
    const { destinations, loading } = useData();
    useViewTracker("page", "/destinations");

    /* Sort: featured first, then by displayOrder */
    const sorted = [...(destinations || [])].sort((a, b) => {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return (a.displayOrder ?? 100) - (b.displayOrder ?? 100);
    });

    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://southtrip.in/" },
            {
                "@type": "ListItem",
                position: 2,
                name: "Destinations",
                item: "https://southtrip.in/destinations",
            },
        ],
    };

    const itemListJsonLd = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Kerala Destinations",
        itemListElement: sorted.map((d, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "TouristAttraction",
                name: d.title,
                description: d.shortDescription,
                image: d.imageUrl || "/default-kerala-destination.svg",
                url: `https://southtrip.in/destinations/${d.slug}`,
                address: {
                    "@type": "PostalAddress",
                    addressRegion: "Kerala",
                    addressCountry: "IN",
                },
            },
        })),
    };

    return (
        <div data-testid="preview-destinations">
            <SEO
                title={t("seo.destinationsTitle")}
                description={t("seo.destinationsDescription")}
                path="/destinations"
                jsonLd={[breadcrumbJsonLd, itemListJsonLd]}
                keywords="kerala destinations, munnar, alleppey, thekkady, fort kochi, varkala, kovalam, wayanad, kerala places to visit by cab"
            />

            {/* HEADER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-8 sm:pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="hz-pill">
                            <Sparkles className="w-3 h-3 mr-1" /> {sorted.length} hand-picked stops
                        </div>
                        <h1 className="font-hz-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-4 sm:mt-5">
                            {t("seo.destinationsH1")}
                        </h1>
                    </div>
                    <div className="md:col-span-4">
                        <p className="text-[var(--ink-soft)] text-sm sm:text-base leading-relaxed">
                            {t("destinations.intro")}
                        </p>
                    </div>
                </div>
            </section>

            {/* DESTINATION GRID */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10" aria-label="Kerala destinations">
                {loading && sorted.length === 0 ? (
                    <div className="text-center text-[var(--ink-soft)] py-20">{t("common.loading")}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {sorted.map((d, i) => {
                            const isHero = d.isFeatured || (i === 0 && !sorted.some((x) => x.isFeatured));
                            return (
                                <article
                                    key={d.id}
                                    id={d.slug}
                                    className={`hz-card overflow-hidden flex flex-col bg-white border border-[var(--border)] ${
                                        isHero ? "lg:col-span-2 lg:row-span-2" : ""
                                    }`}
                                    data-testid={`destination-${d.slug}`}
                                >
                                    <Link to={`/destinations/${d.slug}`} className="block group">
                                        <div className={`relative overflow-hidden bg-[var(--bg-surface)] ${isHero ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
                                            <SmartImage
                                                src={d.imageUrl}
                                                alt={d.imageAlt || `${d.title}, Kerala — ${d.subtitle}`}
                                                className="w-full h-full transition duration-700 group-hover:scale-105"
                                            />
                                            {d.subtitle && (
                                                <div className="absolute top-3 left-3 hz-pill bg-white/90">
                                                    <MapPin className="w-3 h-3" /> {d.subtitle}
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                    <div className={`${isHero ? "p-7 lg:p-8" : "p-5 sm:p-6"} flex-1 flex flex-col`}>
                                        <Link to={`/destinations/${d.slug}`}>
                                            <h2 className={`font-hz-display tracking-tight leading-tight hover:text-[var(--brand)] transition ${isHero ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>
                                                {d.title}
                                            </h2>
                                        </Link>
                                        <div className="flex items-center gap-3 mt-2 text-xs text-[var(--ink-softer)] flex-wrap">
                                            {d.distanceDuration && (
                                                <span className="flex items-center gap-1">
                                                    <Car className="w-3 h-3" /> {d.distanceDuration}
                                                </span>
                                            )}
                                            {d.bestSeason && (
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> {d.bestSeason}
                                                </span>
                                            )}
                                        </div>
                                        <p className={`text-sm text-[var(--ink-soft)] mt-3 leading-relaxed ${isHero ? "" : "line-clamp-3"}`}>
                                            {d.shortDescription}
                                        </p>
                                        {d.bestFor && d.bestFor.length > 0 && (
                                            <div className="text-xs text-[var(--ink-soft)] mt-3">
                                                <b>Best for:</b> {d.bestFor.join(", ")}
                                            </div>
                                        )}
                                        <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-end gap-3">
                                            <Link
                                                to={`/destinations/${d.slug}`}
                                                className="hz-btn-primary text-xs py-2 px-4"
                                                data-testid={`destination-${d.slug}-detail`}
                                            >
                                                Explore {d.title} <ArrowUpRight className="w-3 h-3" />
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* SEO LONG-FORM (kept) */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="destinations-seo-content">
                <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight">{t("destinations.tipsTitle")}</h2>
                <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
                    Each Kerala destination has its own season, vibe and cab-route logic. Below is the SouthTrip cheatsheet our drivers actually use when planning trips for couples, families and Gulf visitors.
                </p>
                <ul className="text-[var(--ink-soft)] mt-6 space-y-3 leading-relaxed">
                    <li><b>Munnar</b> — pair it with Thekkady on a 4-day Cochin loop. Avoid heavy monsoon (June–August) for road safety.</li>
                    <li><b>Alleppey</b> — book the houseboat jetty pickup directly from Cochin Airport (90 km, 2h). October to March is the dream season.</li>
                    <li><b>Thekkady</b> — leave Cochin by 8 am to make the 16:00 boat safari at Periyar Lake.</li>
                    <li><b>Fort Kochi</b> — a perfect 1-day stop for COK Airport arrivals before heading to Munnar or Alleppey.</li>
                    <li><b>Varkala</b> — easier from Trivandrum (TRV) than Cochin. The cliff-top hosts the prettiest sunsets in Kerala.</li>
                    <li><b>Kovalam</b> — only 15 km from TRV, ideal for short Gulf weekends. Halal restaurants and family-friendly resorts are abundant.</li>
                    <li><b>Wayanad</b> — fly into Calicut (CCJ), not Cochin — saves 4 hours of driving each way.</li>
                </ul>

                <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight mt-14">{t("destinations.moreTitle")}</h2>
                <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">{t("destinations.moreSub")}</p>
                <div className="flex flex-wrap gap-3 mt-6">
                    <Link to="/cab-booking" className="hz-btn-primary text-sm">
                        Book a Kerala cab <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link to="/tour-packages" className="hz-btn-ghost text-sm">
                        Browse tour packages <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link to="/airport-transfers" className="hz-btn-ghost text-sm">
                        Airport transfers <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default PreviewDestinations;
